import { formatDateTime as formatDateTimeInternal } from 'helpers/utils';
import { useAuthenticatedUser } from 'hooks/useAuthenticatedUser';

/**
 * Hook que retorna uma função formatDateTime que usa o timezone da empresa do usuário autenticado.
 * Se o usuário não estiver autenticado ou não tiver timezone definido, usa 'America/Sao_Paulo' como padrão.
 *
 * Esta é a forma recomendada de usar formatDateTime em componentes React, pois não precisa passar o timezone.
 *
 * @returns {Function} Função formatDateTime que aceita uma data e retorna a data formatada no timezone da empresa
 *
 * @example
 * const formatDateTime = useFormatDateTime();
 * const formattedDate = formatDateTime('2024-01-01T12:00:00Z');
 */
export const useFormatDateTime = () => {
  const { authenticatedUser } = useAuthenticatedUser();

  const timeZone = authenticatedUser?.companyTimezone || 'America/Sao_Paulo';

  return date => formatDateTimeInternal(date, timeZone);
};

/**
 * Hook que retorna uma função formatDateTime sem precisar passar o timezone.
 * Esta função usa automaticamente o timezone da empresa do usuário autenticado.
 *
 * Use esta função em componentes React para formatar datas usando o timezone da empresa automaticamente.
 *
 * @example
 * import { formatDateTime } from 'hooks/useFormatDateTime';
 * const formatDate = formatDateTime();
 * const formatted = formatDate('2024-01-01T12:00:00Z');
 */
export const formatDateTime = () => {
  return useFormatDateTime();
};
